/**
 * Browser-only KLIPY GIF search integration.
 *
 * KLIPY requires API requests and media loads to originate in the end-user
 * client. Returned media URLs are preserved exactly and never proxied, cached,
 * downloaded, or re-hosted by holito.
 */

import { getEnv } from './utils/env';

/** Normalized KLIPY result consumed by the authoring picker. */
export interface KlipyGif {
  /** Stable KLIPY content id. */
  id: string;
  /** Stable KLIPY content slug used by the share endpoint. */
  slug: string;
  /** Accessible title supplied by KLIPY. */
  title: string;
  /** Small animated rendition for the picker grid. */
  previewUrl: string;
  /** High-quality animated rendition persisted in the information block. */
  imageUrl: string;
  /** Static rendition used by the animation pause control. */
  imageStillUrl: string;
  /** MIME type of the selected animated rendition. */
  mimeType: 'image/webp' | 'image/gif';
  /** Exact query which produced this result. Empty means trending. */
  query: string;
}

/** Options for a client-side KLIPY query. */
export interface FetchKlipyGifsOptions {
  query?: string;
  signal?: AbortSignal;
  /** Explicit key for tests. Defaults to the public Vite setting. */
  apiKey?: string;
  /** Result count supported by KLIPY's search and trending endpoints. */
  limit?: number;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object'
    ? value as Record<string, unknown>
    : null;
}

function stringAt(value: unknown, key: string): string {
  const object = record(value);
  const candidate = object?.[key];
  return typeof candidate === 'string' ? candidate : '';
}

function mediaUrl(files: Record<string, unknown>, size: string, format: string): string {
  return stringAt(record(record(files[size])?.[format]), 'url');
}

function firstUrl(...values: string[]): string {
  return values.find((value) => value.length > 0) ?? '';
}

function normalizeGif(value: unknown, query: string): KlipyGif | null {
  const raw = record(value);
  const files = record(raw?.file);
  const idValue = raw?.id;
  const id = typeof idValue === 'number' || typeof idValue === 'string' ? String(idValue) : '';
  const slug = stringAt(raw, 'slug');
  if (!raw || !files || !id || !slug) return null;

  const previewUrl = firstUrl(
    mediaUrl(files, 'sm', 'webp'),
    mediaUrl(files, 'xs', 'webp'),
    mediaUrl(files, 'sm', 'gif'),
    mediaUrl(files, 'md', 'webp'),
  );
  const webpUrl = firstUrl(mediaUrl(files, 'hd', 'webp'), mediaUrl(files, 'md', 'webp'), previewUrl);
  const gifUrl = firstUrl(mediaUrl(files, 'hd', 'gif'), mediaUrl(files, 'md', 'gif'));
  const imageUrl = webpUrl || gifUrl;
  const imageStillUrl = firstUrl(
    mediaUrl(files, 'hd', 'jpg'),
    mediaUrl(files, 'md', 'jpg'),
    mediaUrl(files, 'sm', 'jpg'),
  );
  if (!previewUrl || !imageUrl || !imageStillUrl) return null;

  const title = stringAt(raw, 'title') || slug.replace(/[-_]+/g, ' ');
  return {
    id,
    slug,
    title,
    previewUrl,
    imageUrl,
    imageStillUrl,
    mimeType: webpUrl ? 'image/webp' : 'image/gif',
    query,
  };
}

function apiKey(value?: string): string {
  return value ?? getEnv('VITE_KLIPY_API_KEY');
}

/** Load trending or searched GIFs directly from KLIPY in API order. */
export async function fetchKlipyGifs(options: FetchKlipyGifsOptions = {}): Promise<KlipyGif[]> {
  const query = options.query ?? '';
  if (query.length > 50) throw new Error('KLIPY search terms are limited to 50 characters');
  const endpoint = query.length > 0 ? 'search' : 'trending';
  const key = encodeURIComponent(apiKey(options.apiKey));
  const url = new URL(`https://api.klipy.com/api/v1/${key}/gifs/${endpoint}`);
  url.searchParams.set('page', '1');
  url.searchParams.set('per_page', String(Math.min(Math.max(options.limit ?? 24, 8), 50)));
  url.searchParams.set('content_filter', 'high');
  url.searchParams.set('format_filter', 'webp,jpg');
  if (query.length > 0) url.searchParams.set('q', query);

  const response = await fetch(url, { signal: options.signal });
  if (!response.ok) throw new Error(`KLIPY request failed (${response.status})`);
  const body = record(await response.json());
  const data = record(body?.data);
  if (body?.result !== true || !Array.isArray(data?.data)) {
    throw new Error('KLIPY returned an invalid response');
  }
  return data.data.map((item) => normalizeGif(item, query)).filter((gif): gif is KlipyGif => gif !== null);
}

/** Retrieve exactly one saved provider item. Never substitute search or trending results. */
export async function fetchKlipyGifBySlug(slug: string, signal?: AbortSignal): Promise<KlipyGif | null> {
  const url = new URL(`https://api.klipy.com/api/v1/${encodeURIComponent(apiKey())}/gifs/items`);
  url.searchParams.set('slugs', slug);
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`KLIPY item request failed (${response.status})`);
  const body = record(await response.json());
  const data = record(body?.data);
  if (body?.result !== true || !Array.isArray(data?.data)) throw new Error('KLIPY returned an invalid item response');
  if (data.data.length === 0) return null;
  if (data.data.length !== 1) throw new Error('KLIPY returned unexpected items');
  const gif = normalizeGif(data.data[0], '');
  if (!gif || gif.slug !== slug) throw new Error('KLIPY returned a different or invalid item');
  return gif;
}

/** Register that a KLIPY search result was selected, without delaying the picker. */
export function registerKlipyShare(gif: Pick<KlipyGif, 'slug' | 'query'>, explicitApiKey?: string): void {
  const key = encodeURIComponent(apiKey(explicitApiKey));
  const slug = encodeURIComponent(gif.slug);
  void fetch(`https://api.klipy.com/api/v1/${key}/gifs/share/${slug}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ q: gif.query }),
    keepalive: true,
  }).catch(() => {
    // Provider analytics must never block selection or surface an author error.
  });
}
